import { useState, useReducer, useEffect, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import isEmail from 'validator/lib/isEmail'
import isLength from 'validator/lib/isLength'
import { axiosBase as axios } from 'utils/functions'
import { displayToast } from 'actions/Dashboard'
import { useAppDispatch } from 'hooks'
import {
  OnboardFormStateType,
  CustomerRegion,
  ActionTypeTypes,
  ActionInputTypes,
} from './types'

export const useSignUpCardholderForm = (accessCodeParam: string | null) => {
  const initialState: OnboardFormStateType = {
    firstName: '',
    lastName: '',
    email: '',
    accessCode: accessCodeParam || '',
    password: '',
    passwordConfirm: '',
    customerRegion: null,
    companyName: '',
    companyUrl: '',
    streetAddress: '',
    aptNumber: '',
    city: '',
    stateProvince: '',
    zipPostalCode: '',
    cardNumber: '',
    errors: {},
  }

  type ErrorsType = { [key in ActionInputTypes]?: string } & {
    [key: string]: string | undefined
  }

  const { t } = useTranslation('pub', {
    keyPrefix: 'PublicRoutes.signUp',
  })

  const sanitizeZipPostalCode = (value: unknown): string =>
    String(value ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^A-Za-z0-9 -]/g, '')
      .slice(0, 10)

  const validateField = (
    field: ActionInputTypes,
    value: any,
    snapshot: OnboardFormStateType,
  ): string | undefined => {
    switch (field) {
      case 'email':
        if (!isLength(String(value), { min: 1, max: 160 }))
          return t('emailRequired')
        if (!isEmail(String(value))) return t('invalidEmail')
        return
      case 'firstName':
        if (!isLength(String(value), { min: 1, max: 50 }))
          return t('firstNameRequired')
        return
      case 'lastName':
        if (!isLength(String(value), { min: 1, max: 50 }))
          return t('lastNameRequired')
        return
      case 'password':
        if (!isLength(String(value), { min: 15, max: 128 }))
          return t('invalidPassword')
        return
      case 'passwordConfirm':
        if (snapshot.password && String(value) !== snapshot.password)
          return t('invalidPasswordConfirm')
        return
      case 'customerRegion':
        if (value == null) return t('customerRegionRequired')
        return
      case 'companyName':
        if (!isLength(String(value), { min: 1, max: 160 }))
          return t('companyNameRequired', 'Company name is required')
        return
      case 'companyUrl':
        if (!isLength(String(value), { min: 1, max: 255 }))
          return t('companyUrlRequired', 'Company URL is required')
        return
      case 'streetAddress':
        if (!isLength(String(value), { min: 1, max: 255 }))
          return t('streetAddressRequired', 'Street address is required')
        return
      case 'city':
        if (!isLength(String(value), { min: 1, max: 100 }))
          return t('cityRequired', 'City is required')
        return
      case 'stateProvince':
        if (!isLength(String(value), { min: 1, max: 100 }))
          return t('stateProvinceRequired', 'State/Province is required')
        return
      case 'zipPostalCode':
        if (!isLength(String(value), { min: 1, max: 10 }))
          return t('zipPostalCodeRequired', 'ZIP/Postal code is required')
        return
      default:
        return
    }
  }

  const reducer = (
    state: OnboardFormStateType,
    action: {
      type: ActionTypeTypes
      input: ActionInputTypes
      value?: string | number
      msg?: string
    },
  ): OnboardFormStateType => {
    switch (action.type) {
      case 'SET_INPUT': {
        const rawValue = action.value
        const coerced =
          action.input === 'customerRegion'
            ? rawValue === ''
              ? null
              : Number(rawValue)
            : action.input === 'zipPostalCode'
              ? sanitizeZipPostalCode(rawValue)
            : String(rawValue ?? '')
        const nextState: OnboardFormStateType = {
          ...state,
          [action.input]: coerced,
          errors: { ...(state.errors as ErrorsType) },
        }

        const fieldError = validateField(action.input, coerced, nextState)
        if (fieldError) {
          ;(nextState.errors as ErrorsType)[action.input] = fieldError
        } else {
          delete (nextState.errors as ErrorsType)[action.input]
        }

        if (action.input === 'password') {
          const pcErr = validateField(
            'passwordConfirm',
            nextState.passwordConfirm,
            nextState,
          )
          if (pcErr) (nextState.errors as ErrorsType).passwordConfirm = pcErr
          else delete (nextState.errors as ErrorsType).passwordConfirm
        } else if (action.input === 'passwordConfirm') {
          const pcErr = validateField('passwordConfirm', coerced, nextState)
          if (pcErr) (nextState.errors as ErrorsType).passwordConfirm = pcErr
          else delete (nextState.errors as ErrorsType).passwordConfirm
        }

        return nextState
      }
      case 'SET_ERRORS':
        return {
          ...state,
          errors: {
            ...(state.errors as ErrorsType),
            [action.input]: action.msg,
          },
        }
      default:
        throw new Error()
    }
  }

  const [state, localDispatch] = useReducer(reducer, initialState)
  const [submitting, setSubmitting] = useState(false)
  const [submittingStep3, setSubmittingStep3] = useState(false)
  const [agree, setAgree] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [customerRegions, setCustomerRegions] = useState<CustomerRegion[]>([])
  const [checkingEligibility, setCheckingEligibility] = useState(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    const getCustomerRegions = async () => {
      try {
        const { data }: { data: CustomerRegion[] } = await axios.get(
          '/mcr/customer-regions',
          {
            baseURL: process.env.REACT_APP_AUTH_URL,
          },
        )
        setCustomerRegions(data)
      } catch (e) {
        // Handle error silently
      }
    }
    getCustomerRegions()
  }, [])

  const setErrors = ({
    input,
    msg,
  }: {
    input: ActionInputTypes
    msg: string
  }) => localDispatch({ type: 'SET_ERRORS', input, msg })

  const validateStep = (step: number): boolean => {
    let isValid = true

    if (step === 2) {
      if (!isEmail(state.email)) {
        setErrors({ input: 'email', msg: t('invalidEmail') })
        isValid = false
      }
      if (!isLength(state.email, { min: 1, max: 160 })) {
        setErrors({ input: 'email', msg: t('emailRequired') })
        isValid = false
      }
      if (!isLength(state.firstName, { min: 1, max: 50 })) {
        setErrors({ input: 'firstName', msg: t('firstNameRequired') })
        isValid = false
      }
      if (!isLength(state.lastName, { min: 1, max: 50 })) {
        setErrors({ input: 'lastName', msg: t('lastNameRequired') })
        isValid = false
      }
      if (!isLength(state.password, { min: 15, max: 128 })) {
        setErrors({ input: 'password', msg: t('invalidPassword') })
        isValid = false
      }
      if (state.password !== state.passwordConfirm) {
        setErrors({
          input: 'passwordConfirm',
          msg: t('invalidPasswordConfirm'),
        })
        isValid = false
      }
      if (!state.customerRegion) {
        setErrors({ input: 'customerRegion', msg: t('customerRegionRequired') })
        isValid = false
      }
      if (!agree) {
        isValid = false
      }
      return isValid
    }

    if (step === 3) {
      if (!isLength(state.companyName, { min: 1, max: 160 })) {
        setErrors({
          input: 'companyName',
          msg: t('companyNameRequired', 'Company name is required'),
        })
        isValid = false
      }
      if (!isLength(state.companyUrl, { min: 1, max: 255 })) {
        setErrors({
          input: 'companyUrl',
          msg: t('companyUrlRequired', 'Company URL is required'),
        })
        isValid = false
      }
      if (!isLength(state.streetAddress, { min: 1, max: 255 })) {
        setErrors({
          input: 'streetAddress',
          msg: t('streetAddressRequired', 'Street address is required'),
        })
        isValid = false
      }
      if (!isLength(state.city, { min: 1, max: 100 })) {
        setErrors({ input: 'city', msg: t('cityRequired', 'City is required') })
        isValid = false
      }
      if (!isLength(state.stateProvince, { min: 1, max: 100 })) {
        setErrors({
          input: 'stateProvince',
          msg: t('stateProvinceRequired', 'State/Province is required'),
        })
        isValid = false
      }
      if (!isLength(state.zipPostalCode, { min: 1, max: 10 })) {
        setErrors({
          input: 'zipPostalCode',
          msg: t('zipPostalCodeRequired', 'ZIP/Postal code is required'),
        })
        isValid = false
      }
      return isValid
    }

    return isValid
  }

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!validateStep(1)) {
        return
      }

      setCheckingEligibility(true)
      try {
        const { data } = await axios.get(
          `/api/mcr/access-code/valid?access_code=${state.cardNumber}`,
          { baseURL: process.env.REACT_APP_AUTH_URL },
        )

        if (data.eligible) {
          dispatch(
            displayToast({
              infoType: 'success',
              title: t('eligibilityVerified', 'Eligibility Verified'),
            }),
          )
          setCurrentStep(currentStep + 1)
        } else {
          dispatch(
            displayToast({
              infoType: 'danger',
              title: t('eligibilityFailed', 'Eligibility Check Failed'),
              message: t(
                'notEligible',
                'Your card is not eligible for this benefit.',
              ),
            }),
          )
        }
      } catch (e: any) {
        dispatch(
          displayToast({
            infoType: 'danger',
            title: t('eligibilityError', 'Error Checking Eligibility'),
            message: t(
              'eligibilityErrorMessage',
              'Unable to verify your card eligibility. Please try again or contact support.',
            ),
          }),
        )
      } finally {
        setCheckingEligibility(false)
      }
    } else if (currentStep === 2) {
      if (!validateStep(2)) {
        return
      }

      setSubmitting(true)
      try {
        const requestBody = {
          firstName: state.firstName,
          lastName: state.lastName,
          email: state.email,
          password: state.password,
          passwordConfirm: state.passwordConfirm,
          customerRegion: state.customerRegion,
          uuid: state.cardNumber,
        }
        await axios.post('/api/mcr/customer/cardholder-benefit', requestBody, {
          baseURL: process.env.REACT_APP_AUTH_URL,
        })
        setCurrentStep(currentStep + 1)
      } catch (e: any) {
        dispatch(
          displayToast({
            infoType: 'danger',
            title: t('accountCreationError', 'Error Creating Account'),
            message: t(
              'accountCreationErrorMessage',
              'Unable to create your account. Please try again or contact support.',
            ),
          }),
        )
      } finally {
        setSubmitting(false)
      }
    }
  }

  const handleWidgetStep1Success = (cardNumberId: string) => {
    localDispatch({
      type: 'SET_INPUT',
      value: cardNumberId,
      input: 'cardNumber',
    })
    setCurrentStep(2)
  }

  const handleStep3Submit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateStep(3)) {
      return
    }

    setSubmittingStep3(true)
    try {
      const requestBody = {
        name: state.companyName,
        url: state.companyUrl,
        address: `${state.streetAddress}${
          state.aptNumber ? ', ' + state.aptNumber : ' '
        }, ${state.city}, ${state.stateProvince}, ${state.zipPostalCode}`,
      }
      await axios.post('/mcr/toe-request', requestBody, {
        baseURL: process.env.REACT_APP_AUTH_URL,
      })
      setCurrentStep(4)
    } catch (e: any) {
      dispatch(
        displayToast({
          infoType: 'danger',
          title: t(
            'companySubmissionError',
            'Error Submitting Company Details',
          ),
          message: t(
            'companySubmissionErrorMessage',
            'Unable to submit your company details. Please try again or contact support.',
          ),
        }),
      )
    } finally {
      setSubmittingStep3(false)
    }
  }

  const canProceedStep2 =
    state.email.length > 0 &&
    !state.errors.email &&
    state.firstName.length > 0 &&
    !state.errors.firstName &&
    state.lastName.length > 0 &&
    !state.errors.lastName &&
    state.password.length >= 15 &&
    !state.errors.password &&
    state.password === state.passwordConfirm &&
    !state.errors.passwordConfirm &&
    state.customerRegion !== null &&
    !state.errors.customerRegion &&
    agree

  const canProceedStep3 =
    !state.errors.companyName &&
    !state.errors.companyUrl &&
    !state.errors.streetAddress &&
    !state.errors.city &&
    !state.errors.stateProvince &&
    !state.errors.zipPostalCode &&
    isLength(state.companyName, { min: 1, max: 160 }) &&
    isLength(state.companyUrl, { min: 1, max: 255 }) &&
    isLength(state.streetAddress, { min: 1, max: 255 }) &&
    isLength(state.city, { min: 1, max: 100 }) &&
    isLength(state.stateProvince, { min: 1, max: 100 }) &&
    isLength(state.zipPostalCode, { min: 1, max: 10 })

  return {
    state,
    localDispatch,
    submitting,
    submittingStep3,
    agree,
    setAgree,
    currentStep,
    customerRegions,
    checkingEligibility,
    handleWidgetStep1Success,
    handleNext,
    handleStep3Submit,
    canProceedStep2,
    canProceedStep3,
  }
}
